import { createFileRoute, Outlet, Link } from '@tanstack/react-router';
import { z } from 'zod';
import { cn } from '@/lib/utils';

const SalesSearch = z.object({
  from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  /** Source DB name (= store identity). E.g. "DALEEP_20260501". */
  store: z.string().regex(/^[A-Za-z0-9_]{1,64}$/).optional(),
  page: z.coerce.number().int().min(1).optional(),
  // Salesmen-tab-specific filters — declared here so the parent route's
  // validateSearch keeps them when the tab is active. Stripped via Link
  // search-mappers below when navigating between tabs.
  salesman: z.string().regex(/^[A-Za-z0-9._-]{1,32}$/).optional(),
  user: z.string().regex(/^[A-Za-z0-9 ._-]{1,32}$/).optional(),
  subgroup: z.string().regex(/^[A-Za-z0-9 ]{1,32}$/).optional(),
  // Estimates-tab filter (S = sale-side estimate, P = estimate cancellation, ALL = both)
  ttype: z.enum(['ALL', 'S', 'P']).optional(),
});

export const Route = createFileRoute('/_app/sales')({
  validateSearch: SalesSearch,
  component: SalesLayout,
});

// Carry the cross-tab filters (date + store) across tab navigation; drop
// per-tab params so a stale "salesman" filter doesn't follow into Bills.
type SharedSearch = { from?: string; to?: string; store?: string };
const carryShared = (prev: SharedSearch): SharedSearch => ({
  from: prev.from, to: prev.to, store: prev.store,
});

function SalesLayout() {
  const linkCls = 'rounded-md px-3 py-1.5 text-sm transition-colors';
  const activeCls = 'bg-muted text-foreground';
  return (
    <div className="space-y-4">
      <nav className="flex items-center gap-2 border-b pb-2">
        <Link to="/sales/dashboard" search={carryShared} className={cn(linkCls, 'text-muted-foreground hover:text-foreground')} activeProps={{ className: cn(linkCls, activeCls) }}>Dashboard</Link>
        <Link to="/sales/bills" search={carryShared} className={cn(linkCls, 'text-muted-foreground hover:text-foreground')} activeProps={{ className: cn(linkCls, activeCls) }}>Bills</Link>
        <Link to="/sales/estimates" search={carryShared} className={cn(linkCls, 'text-muted-foreground hover:text-foreground')} activeProps={{ className: cn(linkCls, activeCls) }}>Estimates</Link>
        <Link to="/sales/salesmen" search={carryShared} className={cn(linkCls, 'text-muted-foreground hover:text-foreground')} activeProps={{ className: cn(linkCls, activeCls) }}>Salesmen</Link>
      </nav>
      <Outlet />
    </div>
  );
}
